import React, { ReactNode } from 'react';

import { NewRelic } from '../hooks/useNewRelic';
import { CriticalApplicationError } from '../types/errors';

// A client 500 error boundary represents an error caught late enough that the whole page crashes
// and a user can't work around the error
class Client500 extends React.Component<{ children: ReactNode }> {
  state: { hasError: boolean } = {
    hasError: false,
  };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    try {
      const appVersion = process.env.NEXT_PUBLIC_VERSION || 'unknown';
      // Create a CriticalApplicationError with the caught error as inner error
      const criticalError = new CriticalApplicationError(
        `Application render error: ${error.message}`,
        'client',
        error,
      );

      // Use the original simple error reporting logic
      if (typeof window == 'undefined') {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const newrelic = require('newrelic');
        newrelic?.noticeError(criticalError, { appVersion });
      } else {
        console.log('NEWRELIC NOTICE ERROR', criticalError);
        NewRelic()?.noticeError(criticalError);
      }

      // Log additional context for debugging
      // eslint-disable-next-line no-console
      console.error('Critical application error:', criticalError);
      // eslint-disable-next-line no-console
      console.error('Component stack:', errorInfo.componentStack);
    } catch (err) {}
  }

  render() {
    if (this.state.hasError) {
      // Fallback error page
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            width: '100%',
            backgroundColor: '#F9F3EB',
          }}
        >
          <h2 style={{ width: '100%', textAlign: 'center', margin: '20px' }}>
            Oops, there is an error!
          </h2>
          <h3 style={{ width: '100%', textAlign: 'center', margin: '20px' }}>
            We have informed our developers, try again.
          </h3>
          <span style={{ width: '100%', textAlign: 'center' }}>
            If this persists please contact us in the chat or email{' '}
            <span>
              <a href="mailto:support@curipod.com" style={{ color: 'blue ' }}>
                support@curipod.com
              </a>
            </span>
          </span>

          <button
            onClick={() => window.location.reload()}
            style={{
              margin: '20px',
              border: 'none',
              backgroundColor: '#33A0DD',
              borderRadius: '10px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '24px',
              padding: '20px',
              color: 'white',
            }}
          >
            Try again
          </button>
        </div>
      );
    }

    // All is good
    return this.props.children;
  }
}

export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
): React.ComponentType<P> => {
  const WrappedComponent = (props: P) => (
    <Client500>
      <Component {...props} />
    </Client500>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName ||
    Component.name})`;

  return WrappedComponent;
};

export default Client500;
